import { reflect } from '@effector/reflect';
import { Select } from '@mantine/core';
import { DatePickerInput, DatePickerPreset } from '@mantine/dates';
import dayjs from 'dayjs';
import { combine } from 'effector';
import { TIME_ZONES } from 'shared/config';
import { $t, breakpoints, DateFilterModel, translated } from 'shared/lib';

export const createDateFilter = ({ dateFilter, dateFilterDropdownClosed }: DateFilterModel) => {
	const DateField = reflect({
		view: DatePickerInput,
		bind: {
			closeOnChange: false,
			type: 'range',
			valueFormat: 'YYYY-MM-DD',
			label: translated('date-filter.date.label'),
			maxDate: new Date(),
			value: dateFilter.$value,
			onChange: (ranges) => dateFilter.setValue([ranges && ranges[0], ranges && ranges[1]]),
			onDropdownClose: dateFilterDropdownClosed,
			allowSingleDateInRange: true,
			highlightToday: true,
			presets: combine(
				[$t, breakpoints.sm],
				([t, sm]) =>
					sm
						? ([
								{
									label: t('date-filter.date.today'),
									value: [dayjs().format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')],
								},
								{
									label: t('date-filter.date.yesterday'),
									value: [
										dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
										dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
									],
								},
								{
									label: t('date-filter.date.last-7-days'),
									value: [
										dayjs().subtract(7, 'day').format('YYYY-MM-DD'),
										dayjs().format('YYYY-MM-DD'),
									],
								},
								{
									label: t('date-filter.date.last-30-days'),
									value: [
										dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
										dayjs().format('YYYY-MM-DD'),
									],
								},
								{
									label: t('date-filter.date.this-month'),
									value: [
										dayjs().startOf('month').format('YYYY-MM-DD'),
										dayjs().format('YYYY-MM-DD'),
									],
								},
								{
									label: t('date-filter.date.last-month'),
									value: [
										dayjs().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
										dayjs().subtract(1, 'month').endOf('month').format('YYYY-MM-DD'),
									],
								},
							] as DatePickerPreset<'range'>[])
						: undefined,
				{ skipVoid: false },
			),
		},
	});

	const TimeZoneField = reflect({
		view: Select,
		bind: {
			'data-test-id': 'date-filter-time-zone-select',
			label: translated('date-filter.time-zone.label'),
			placeholder: translated('date-filter.time-zone.placeholder'),
			data: Object.entries(TIME_ZONES).map(([key, value]) => ({
				value: value.toString(),
				label: key,
			})),
			clearable: false,
		},
	});

	return { DateField, TimeZoneField };
};
